import { Link } from "react-router-dom";
import { useConfig } from "@/contexts/ConfigContext";
import { Scale, HeartHandshake, Users, ArrowUpRight, ShieldCheck, Sparkles, ChevronLeft } from "lucide-react";

/* ═══════════════════════════════════════════════════════════════
   /register · página de selección de tipo de cuenta
   Cada card lleva a /registro/{abogado|mediador|solicitante}
   con el formulario dedicado (sin tabs).
   ═══════════════════════════════════════════════════════════════ */

export default function RegisterChooser() {
  const { config } = useConfig();

  return (
    <div className="container-app py-16 max-w-5xl" data-testid="register-chooser-page">
      <Link to="/" className="text-xs text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mb-4" data-testid="chooser-back">
        <ChevronLeft size={12}/> Volver al inicio
      </Link>

      <div className="text-center max-w-2xl mx-auto">
        <div className="label-eyebrow text-cyan-300 inline-flex items-center gap-2">
          <Sparkles size={12}/> Crear cuenta Colega Link
        </div>
        <h1 className="serif text-5xl md:text-6xl mt-4 leading-[0.95] tracking-tight">
          ¿Qué tipo de cuenta<br/>
          <span className="brand-gradient-text">necesitas?</span>
        </h1>
        <p className="mt-5 text-sm text-muted-foreground leading-relaxed">
          Selecciona tu perfil para acceder al formulario dedicado. Cada tipo tiene requisitos y beneficios distintos.
        </p>
      </div>

      <div className="mt-12 grid md:grid-cols-3 gap-5">
        {/* Abogado/a */}
        <RoleCard
          to="/registro/abogado"
          testid="chooser-abogado"
          icon={Scale}
          accent="cyan"
          title="Soy Abogado/a"
          headline="Publica y toma delegaciones"
          bullets={[
            "Publicar delegaciones para colegas",
            "Postular a delegaciones abiertas",
            "Colega IA (con membresía)",
            "Biblioteca Jurídica colaborativa",
          ]}
          cta="Crear cuenta de Abogado/a"
        />

        {/* Mediador/a */}
        <RoleCard
          to="/registro/mediador"
          testid="chooser-mediador"
          icon={ShieldCheck}
          accent="emerald"
          title="Soy Mediador/a"
          headline="Aparece en el Directorio verificado"
          bullets={[
            "Publica tarifario transparente",
            "Recibe solicitudes de mediación",
            "Pago Protegido Colega Link",
            "Requiere N° Registro Min. Justicia",
          ]}
          cta="Crear cuenta de Mediador/a"
        />

        {/* Persona solicitante */}
        {config.new_mediation_enabled ? (
          <RoleCard
            to="/registro/solicitante"
            testid="chooser-solicitante"
            icon={HeartHandshake}
            accent="amber"
            title="Necesito Mediación"
            headline="Encuentra mediador familiar privado"
            bullets={[
              "Solicita mediación en 3 pasos",
              "Tarifas verificadas y transparentes",
              "Pago Protegido: liberación por etapas",
              "Sin costo hasta contratar",
            ]}
            cta="Crear cuenta de Solicitante"
          />
        ) : (
          <div className="panel p-6 opacity-60 flex flex-col items-center justify-center text-center min-h-[280px]">
            <Users size={26} className="text-muted-foreground mb-3"/>
            <div className="text-sm text-muted-foreground">Registro de personas solicitantes próximamente.</div>
          </div>
        )}
      </div>

      <div className="mt-14 text-center text-xs text-muted-foreground" data-testid="chooser-footer">
        ¿Ya tienes cuenta?{" "}
        <Link to="/login" className="text-cyan-300 hover:text-cyan-200 underline" data-testid="chooser-login-link">
          Ingresar
        </Link>
      </div>
    </div>
  );
}

function RoleCard({ to, testid, icon: Icon, accent, title, headline, bullets, cta }) {
  const accentStyles = {
    cyan:    { ring: "hover:ring-cyan-400/50 hover:border-cyan-400/40",       icon: "text-cyan-300",    dot: "bg-cyan-400" },
    emerald: { ring: "hover:ring-emerald-400/50 hover:border-emerald-400/40", icon: "text-emerald-300", dot: "bg-emerald-400" },
    amber:   { ring: "hover:ring-amber-400/50 hover:border-amber-400/40",     icon: "text-amber-300",   dot: "bg-amber-400" },
  }[accent] || { ring: "", icon: "text-cyan-300", dot: "bg-cyan-400" };

  return (
    <Link
      to={to}
      data-testid={testid}
      className={`panel p-6 flex flex-col transition-all hover:ring-1 ${accentStyles.ring} group`}
    >
      <div className={`w-12 h-12 rounded-xl border ${accent === 'amber' ? 'border-amber-400/40 bg-amber-400/10' : accent === 'emerald' ? 'border-emerald-400/40 bg-emerald-400/10' : 'border-cyan-400/40 bg-cyan-400/10'} flex items-center justify-center`}>
        <Icon size={22} className={accentStyles.icon}/>
      </div>
      <h3 className="serif text-2xl mt-4">{title}</h3>
      <div className="text-xs text-muted-foreground mt-1">{headline}</div>

      <ul className="mt-5 space-y-2 flex-1">
        {bullets.map((b) => (
          <li key={b} className="text-xs text-foreground/80 flex items-start gap-2">
            <span className={`inline-block w-1.5 h-1.5 rounded-full ${accentStyles.dot} mt-1.5 shrink-0`}/>
            {b}
          </li>
        ))}
      </ul>

      <div className="mt-6 pt-4 border-t border-white/10">
        <div className="inline-flex items-center gap-1.5 text-sm font-medium group-hover:text-foreground text-foreground/90">
          {cta} <ArrowUpRight size={13}/>
        </div>
      </div>
    </Link>
  );
}
